import { FilterValueRepository } from '@/repository/FilterValueRepository';
import { FilterValue } from '@/API';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition';
import {
  FilterRowSelectionValue,
  FilterSelections,
  RelationalOperator,
} from '@lambda-layer/types/filter';
import { GenericService } from '@/service/GenericService';

export interface FilterValueConfig {
  fieldId: string;
  relationalOperator: RelationalOperator;
  value: FilterRowSelectionValue[];
}

export class FilterValueService extends GenericService<FilterValue> {
  protected repository: FilterValueRepository;

  constructor(repository: FilterValueRepository) {
    super(repository);
    this.repository = repository;
  }

  /**
   * Gets default filter configurations for a specific list type
   */
  async getFilterValuesForListType(
    listType: string
  ): Promise<FilterValueConfig[]> {
    const defaultValues =
      await this.repository.getDefaultFilterValuesByListType(listType);
    return this.mapDefaultValues(defaultValues);
  }

  /**
   * Gets default filter configuration for a specific field
   */
  async getFilterValuesForField(
    listType: string,
    fieldId: string
  ): Promise<FilterValueConfig[]> {
    const defaultValues = await this.repository.getDefaultFilterValuesByField(
      listType,
      fieldId
    );
    return this.mapDefaultValues(defaultValues);
  }

  /**
   * Applies default filter values to filter definitions
   */
  async applyDefaultFilters(
    listType: string,
    filterDefinitions: FilterDefinition[]
  ): Promise<FilterDefinition[]> {
    const defaultValues =
      await this.repository.getDefaultFilterValuesByListType(listType);

    if (defaultValues.length === 0) {
      return filterDefinitions;
    }

    // Create lookup map for efficient access
    const defaultsByFieldId = this.groupDefaultsByFieldId(defaultValues);

    return filterDefinitions.map(filterDef => {
      const defaults = defaultsByFieldId.get(filterDef.id);
      if (!defaults?.length) {
        return filterDef;
      }

      // Find the primary default (highest priority or first one)
      const primaryDefault = defaults[0];
      if (primaryDefault) {
        filterDef.defaultValue = primaryDefault.selectedValues;
      }

      return filterDef;
    });
  }

  /**
   * Generates default filter selections based on database configurations
   */
  async generateDefaultFilterSelections(
    listType: string,
    filterDefinitions: FilterDefinition[]
  ): Promise<FilterSelections> {
    const defaultValues =
      await this.repository.getDefaultFilterValuesByListType(listType);

    const selections: FilterSelections = {};
    const validFieldIds = new Set(filterDefinitions.map(def => def.id));

    defaultValues.forEach((defaultValue: FilterValue) => {
      // Only apply defaults for fields that exist in the filter definitions
      if (validFieldIds.has(defaultValue.fieldId)) {
        selections[defaultValue.fieldId] = {
          relationalOperator: this.parseRelationalOperator(
            defaultValue.relationalOperator
          ),
          value: defaultValue.selectedValues || [],
        };
      }
    });

    return selections;
  }

  /**
   * Maps default values to service configurations
   */
  private mapDefaultValues(defaultValues: FilterValue[]): FilterValueConfig[] {
    return defaultValues.map(defaultValue => ({
      fieldId: defaultValue.fieldId,
      relationalOperator: this.parseRelationalOperator(
        defaultValue.relationalOperator
      ),
      value: defaultValue.selectedValues || [],
    }));
  }

  /**
   * Groups default values by field ID for efficient lookup
   */
  private groupDefaultsByFieldId(
    defaultValues: FilterValue[]
  ): Map<string, FilterValue[]> {
    const grouped = new Map<string, FilterValue[]>();

    defaultValues.forEach(defaultValue => {
      const existing = grouped.get(defaultValue.fieldId) || [];
      existing.push(defaultValue);
      grouped.set(defaultValue.fieldId, existing);
    });

    return grouped;
  }

  /**
   * Converts string relational operator to enum
   */
  private parseRelationalOperator(
    operator: string | null | undefined
  ): RelationalOperator {
    // Direct enum key lookup
    return (
      RelationalOperator[operator as keyof typeof RelationalOperator] ||
      RelationalOperator.EQUAL
    );
  }
}
